import { Component, Renderer2, ViewChild, OnInit, OnDestroy, HostListener, NgZone } from '@angular/core';
import { CommonModule, NgStyle } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription, Subject } from 'rxjs'; // Added Subject
import { debounceTime } from 'rxjs/operators'; // Added debounceTime
import { AppTopbar } from './app.topbar';
import { AppSidebar } from './app.sidebar';
import { AppFooter } from './app.footer';
import { LayoutService } from '../service/layout.service';
import { DirectionService } from '../../services/direction.service';

@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [CommonModule, NgStyle, AppTopbar, AppSidebar, RouterModule, AppFooter],
    template: `<div class="layout-wrapper" [ngClass]="containerClass">
        <app-topbar></app-topbar>
        <app-sidebar></app-sidebar>
        <div class="layout-main-container" [ngStyle]="getSidebarPositionStyle()">
            <div class="layout-main">
                <router-outlet></router-outlet>
            </div>
            <app-footer></app-footer>
        </div>
        <div class="layout-mask animate-fadein"></div>
    </div>`
})
export class AppLayout implements OnInit, OnDestroy { // Implemented OnInit, OnDestroy
    overlayMenuOpenSubscription: Subscription;
    menuOutsideClickListener: any;

    @ViewChild(AppSidebar) appSidebar!: AppSidebar;
    @ViewChild(AppTopbar) appTopBar!: AppTopbar;

    private resizeSubject = new Subject<void>(); // Added Subject for resize
    private resizeSubscription!: Subscription; // Subscription for resize

    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router,
        public directionService: DirectionService,
        private ngZone: NgZone // Injected NgZone
    ) {
        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
                    if (this.isOutsideClicked(event)) {
                        this.hideMenu();
                    }
                });
            }

            if (this.layoutService.layoutState().staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.hideMenu();
        });
    }

    // NEW: HostListener to capture window resize events
    @HostListener('window:resize')
    onResize() {
        this.resizeSubject.next();
    }

    // NEW: OnInit lifecycle hook
    ngOnInit() {
        // Set up the debounced resize listener outside Angular's zone for performance
        this.ngZone.runOutsideAngular(() => {
            this.resizeSubscription = this.resizeSubject.pipe(
                debounceTime(100) // Debounce time in milliseconds
            ).subscribe(() => {
                this.ngZone.run(() => { // Re-enter Angular's zone to update component properties
                    this.layoutService.onWindowResize();
                });
            });
        });
        // Call onWindowResize once on component initialization to set the initial layout state
        this.layoutService.onWindowResize();
    }

    isOutsideClicked(event: MouseEvent) {
        const sidebarEl = document.querySelector('.layout-sidebar');
        const topbarEl = document.querySelector('.layout-menu-button');
        const eventTarget = event.target as Node;

        return !(sidebarEl?.isSameNode(eventTarget) || sidebarEl?.contains(eventTarget) || topbarEl?.isSameNode(eventTarget) || topbarEl?.contains(eventTarget));
    }

    hideMenu() {
        this.layoutService.layoutState.update((prev) => ({ ...prev, overlayMenuActive: false, staticMenuMobileActive: false, menuHoverActive: false }));
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }
        this.unblockBodyScroll();
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    get containerClass() {
        const layoutConfigValue = this.layoutService.layoutConfig();
        const layoutStateValue = this.layoutService.layoutState();

        return {
            'layout-overlay': layoutConfigValue.menuMode === 'overlay',
            'layout-static': layoutConfigValue.menuMode === 'static',
            'layout-static-inactive': layoutStateValue.staticMenuDesktopInactive && layoutConfigValue.menuMode === 'static',
            'layout-overlay-active': layoutStateValue.overlayMenuActive,
            'layout-mobile-active': layoutStateValue.staticMenuMobileActive
        };
    }

    // UPDATED: ngOnDestroy lifecycle hook to unsubscribe from resize listener
    ngOnDestroy() {
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }
        if (this.resizeSubscription) { // Unsubscribe from resize listener
            this.resizeSubscription.unsubscribe();
        }
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }
    }

    getSidebarPositionStyle() {
        if (!this.layoutService) {
            return {};
        }
        const currentLayoutConfig = this.layoutService.layoutConfig();
        const currentLayoutState = this.layoutService.layoutState();

        if (!currentLayoutConfig || !currentLayoutState) {
            return {};
        }

        const isRtl = this.directionService.isRtl();
        const menuMode = currentLayoutConfig.menuMode;
        const staticMenuDesktopInactive = currentLayoutState.staticMenuDesktopInactive;
        const staticMenuMobileActive = currentLayoutState.staticMenuMobileActive;

        if (menuMode === 'overlay' || (menuMode === 'static' && staticMenuDesktopInactive) || staticMenuMobileActive) {
            return isRtl ? { marginRight: '0', marginLeft: 'auto' } : { marginLeft: '0', marginRight: 'auto' };
        } else {
            return isRtl ? { marginRight: '22rem', marginLeft: 'auto' } : { marginLeft: '22rem', marginRight: 'auto' };
        }
    }
}